import { AnalyticsBrowser } from "@segment/analytics-next";
import type {
  AccountCustomerOutputDto,
  CartItemOutputDto,
  CartItemResponseDto,
  CartOutputDto,
  CouponStatus,
  CustomerOutputDto,
  ItemAdhesive,
  ItemFinish,
  ItemLaminate,
  ItemMarketingName,
  ItemMaterial,
  ItemOrientation,
  ItemShape,
  PaymentMethodType,
  PricingOutputDto,
  ProductPublicDto,
  ProofPageItemDto,
  SaveItemRequest,
  SaveItemResponse,
  SpecVersionOutputPublicDto,
} from "@sticker-giant/sg-frontend-common";
import {
  CookieHelper,
  ItemFormatter,
  ItemProductType,
  SkuNames,
  SkuTranslator,
  ThumbnailGenerator,
} from "@sticker-giant/sg-frontend-common";
import type { SavedInfoSource } from "@/constants/SavedInfoSource";
import { getStepIndividualEventName } from "@/lib/checkout/steps";
import type { PromoSuppressionReason } from "@/lib/emailPromoSuppression";
import { scheduleIdle } from "@/lib/scheduleIdle";
import type { ProductHit } from "@/lib/search/types";
import type { TriggerLabel } from "@/types/contentful/emailPromoModalTrigger";
import type { RollDriver } from "@/utils/rollQuantityUtils";
import type { StackSelection } from "@/utils/stackQuantityUtils";
import { pushDataLayer } from "./datalayer";

export const analytics = new AnalyticsBrowser();

let initialized = false;

export function initAnalytics(writeKey: string) {
  if (!writeKey) {
    throw new Error("Segment write key is missing. Set NEXT_PUBLIC_SEGMENT_WRITE_KEY in your environment.");
  }
  if (initialized) return;
  initialized = true;
  // Defer the network-heavy load to idle. AnalyticsBrowser buffers track/page/
  // identify calls made before load, so no early events are lost.
  scheduleIdle(() => {
    analytics.load({ writeKey }, {});
  });
}

function getFacebookCookies() {
  return {
    fbclid: CookieHelper.getCookie("_fbc"),
    fbp: CookieHelper.getCookie("_fbp"),
  };
}

function translateItemCategory(productSku: ItemProductType) {
  switch (productSku) {
    case ItemProductType.DCU:
    case ItemProductType.KCU:
    case ItemProductType.CLA:
      return "Stickers";
    case ItemProductType.LLA:
    case ItemProductType.PLA:
      return "Labels";
    case ItemProductType.FIN:
      return "Finished Goods";
    default:
      return "";
  }
}

// Item Viewed — 1:1 parity with sg-store ProductDetailsHelper.trackViewItemEvent

interface ItemViewedInput {
  product: ProductPublicDto;
  isSpecLoaded: boolean;
}

export function buildItemViewedPayload({ product, isSpecLoaded }: ItemViewedInput) {
  const startingQuantity = product.quantityOptions[0] || 1;
  return {
    category: translateItemCategory(product.pricingProductType.sku),
    currency: "USD",
    imageUrl: product.productImages[0]?.filepath,
    material: SkuTranslator.getMaterialName(product.defaultMaterial),
    name: SkuTranslator.getProductMarketingName(product.sku),
    price: product.startingPrice,
    product_id: product.sku,
    productName: SkuTranslator.getProductType(product.pricingProductType.sku),
    marketingName: SkuTranslator.getProductMarketingName(product.sku),
    sku: {
      marketingName: product.sku,
      material: product.defaultMaterial,
    },
    url: typeof window !== "undefined" ? window.location.href : "",
    value: product.startingPrice * startingQuantity,
    isSpecLoaded,
    ...getFacebookCookies(),
  };
}

export function trackItemViewed(input: ItemViewedInput) {
  analytics.track("Item Viewed", buildItemViewedPayload(input));
}

// Item Added to Cart — 1:1 parity with sg-store ProductDetailsHelper.trackAddToCartEvent, plus `buyNow` (reorder → checkout)

interface ItemAddedToCartInput {
  cartItemResponse: CartItemResponseDto | undefined;
  isEditMode: boolean;
  buyNow?: boolean;
}

function getFileExtension(fileName?: string) {
  if (!fileName) return undefined;
  const indexOfLastDot = fileName.lastIndexOf(".");
  return fileName.substring(indexOfLastDot);
}

export function buildItemAddedToCartPayload({ cartItemResponse, isEditMode, buyNow }: ItemAddedToCartInput) {
  if (!cartItemResponse) return undefined;

  const { cart, cartItem } = cartItemResponse;

  return {
    orderId: cart.id,
    imageUrl: cartItem.fileUrl,
    adhesive: SkuTranslator.getAdhesiveName(cartItem.sku.adhesive),
    category: translateItemCategory(cartItem.sku.product),
    currency: "USD",
    finish: SkuTranslator.getFinishName(cartItem.sku.finish),
    laminate: SkuTranslator.getLaminateName(cartItem.sku.laminate),
    material: SkuTranslator.getMaterialName(cartItem.sku.material),
    name: SkuTranslator.getProductMarketingName(cartItem.sku.marketingName),
    price: cartItem.itemPrice,
    product_id: cartItem.sku.marketingName,
    reorder: cartItem.isReorder,
    itemId: cartItem.id,
    marketingName: SkuTranslator.getProductMarketingName(cartItem.sku.marketingName),
    productName: SkuTranslator.getProductType(cartItem.sku.product),
    quantity: cartItem.quantity,
    rewindPosition: SkuTranslator.getRewindPositionName(cartItem.sku.orientation),
    rollCount: cartItem.rollCount,
    rollDriver: cartItem.rollDriver,
    rollQty: cartItem.rollQty,
    shape: SkuTranslator.getShapeName(cartItem.sku.shape),
    sku: cartItem.sku,
    url: typeof window !== "undefined" ? window.location.href : "",
    value: cartItem.itemPrice,
    buyNow: !!buyNow,
    dpi: cartItem.dpi,
    fileExt: getFileExtension(cartItem.fileUrl),
    edited: !!isEditMode,
    ...getFacebookCookies(),
  };
}

export function trackItemAddedToCart(input: ItemAddedToCartInput) {
  const payload = buildItemAddedToCartPayload(input);
  if (!payload) return;
  analytics.track("Item Added to Cart", payload);
}

// DPI Calculated — 1:1 parity with sg-store LowDpiHelper.calculateDpi tracking

interface DpiCalculatedInput {
  dpi: number;
  widthPixels: number;
  heightPixels: number;
  widthInches: number;
  heightInches: number;
  pageType: "pdp" | "proof-approval";
}

export function buildDpiCalculatedPayload(input: DpiCalculatedInput) {
  return {
    dpi: input.dpi,
    widthPixels: input.widthPixels,
    heightPixels: input.heightPixels,
    widthInches: input.widthInches,
    heightInches: input.heightInches,
    pageType: input.pageType,
    action: "DPI Calculated" as const,
  };
}

export function trackDpiCalculated(input: DpiCalculatedInput) {
  analytics.track("Item Clicked", buildDpiCalculatedPayload(input));
}

// Proof Approved — 1:1 parity with sg-store ProofSubmitReviewButton.trackProofApproval

interface ProofApprovedInput {
  spec: SpecVersionOutputPublicDto;
  proofPageItem: ProofPageItemDto | undefined;
}

export function buildProofApprovedPayload({ spec, proofPageItem }: ProofApprovedInput) {
  return {
    itemId: spec.orderItemId,
    height: spec.sku.height,
    width: spec.sku.width,
    material: spec.sku.material,
    lamination: proofPageItem?.lamination,
    adhesive: proofPageItem?.itemAdhesive,
    finish: proofPageItem?.itemFinish,
    name: proofPageItem?.marketingName,
    rewindPosition: proofPageItem?.rewindPosition,
    shape: spec.sku.shape,
    category: proofPageItem?.productType,
    quantity: proofPageItem?.quantity,
    sku: proofPageItem?.sku,
  };
}

export function trackProofApproved(input: ProofApprovedInput) {
  analytics.track("Proof Approved", buildProofApprovedPayload(input));
}

// Option Selected — shared emitter for the PDP "<X> Selected" Item Clicked events

function trackOptionSelected(sku: string, name: string, action: string) {
  analytics.track("Item Clicked", { sku, name, pageType: "pdp", action });
}

// Shape Selected — 1:1 parity with sg-store ShapeOption.tsx
export function trackShapeSelected(shape: ItemShape) {
  trackOptionSelected(shape, SkuNames.Shape[shape], "Shape Selected");
}

// Material Selected — 1:1 parity with sg-store MaterialOption.tsx
export function trackMaterialSelected(material: ItemMaterial) {
  trackOptionSelected(material, SkuNames.Material[material], "Material Selected");
}

// Lamination Selected — 1:1 parity with sg-store LaminationOption.tsx
export function trackLaminationSelected(laminate: ItemLaminate) {
  trackOptionSelected(laminate, SkuNames.Laminate[laminate], "Lamination Selected");
}

// Adhesive Selected — new in sg-next (not tracked in sg-store)
export function trackAdhesiveSelected(adhesive: ItemAdhesive) {
  trackOptionSelected(adhesive, SkuNames.Adhesive[adhesive], "Adhesive Selected");
}

// Finish Selected — new in sg-next (not tracked in sg-store)
export function trackFinishSelected(finish: ItemFinish) {
  trackOptionSelected(finish, SkuNames.Finish[finish], "Finish Selected");
}

// Roll Finishing Selected — new in sg-next (not tracked in sg-store)
export function trackOrientationSelected(orientation: ItemOrientation) {
  trackOptionSelected(orientation, SkuNames.RewindPosition[orientation], "Roll Finishing Selected");
}

// Product Selected — new in sg-next (not tracked in sg-store)
export function trackProductSelected(marketingName: ItemMarketingName) {
  trackOptionSelected(marketingName, SkuNames.MarketingName[marketingName], "Product Selected");
}

// Size Selected — 1:1 parity with sg-store SizeOption.tsx
export function trackSizeSelected(size: { value: string; width?: string; height?: string }) {
  if (size.value === "custom") {
    analytics.track("Item Clicked", {
      value: "custom",
      pageType: "pdp",
      action: "Size Selected",
    });
  } else {
    analytics.track("Item Clicked", {
      value: size.value.trim(),
      width: size.width?.trim(),
      height: size.height?.trim(),
      pageType: "pdp",
      action: "Size Selected",
    });
  }
}

// Custom Size Entered — 1:1 parity with sg-store CustomSize.tsx
export function trackCustomSizeEntered(width: string, height: string) {
  analytics.track("Item Clicked", {
    value: `${width} x ${height}`,
    width,
    height,
    pageType: "pdp",
    action: "Custom Size Entered",
  });
}

// Quantity Selected — 1:1 parity with sg-store QuantitySelect.tsx
export function trackQuantitySelected(quantity: number | "custom", pageType: "pdp" | "cart" = "pdp") {
  analytics.track("Item Clicked", {
    quantity,
    pageType,
    action: "Quantity Selected",
  });
}

// Custom Quantity Entered — 1:1 parity with sg-store CustomQuantity.tsx
export function trackCustomQuantityEntered(quantity: number, pageType: "pdp" | "cart" = "pdp") {
  analytics.track("Item Clicked", {
    validatedQuantity: quantity,
    enteredQuantity: quantity,
    pageType,
    action: "Custom Quantity Entered",
  });
}

interface RollQuantitySelection {
  numRolls: number | null;
  itemsPerRoll: number | null;
  outsideDiameter?: string;
}

// Roll Quantity Selected — "Fewest rolls possible" reports a null itemsPerRoll
export function trackRollQuantitySelected(rollOption: RollQuantitySelection, pageType: "pdp" | "cart" = "pdp") {
  analytics.track("Item Clicked", {
    numRolls: rollOption.numRolls,
    itemsPerRoll: rollOption.itemsPerRoll,
    outsideDiameter: rollOption.outsideDiameter,
    pageType,
    action: "Roll Quantity Selected",
  });
}

// Roll Quantity Preference Selected
export function trackRollQuantityDriverSelected(driver: RollDriver, pageType: "pdp" | "cart" = "pdp") {
  analytics.track("Item Clicked", { driver, pageType, action: "Roll Quantity Driver Selected" });
}

export function trackRollQuantityReset(pageType: "pdp" | "cart" = "pdp") {
  analytics.track("Item Clicked", {
    pageType,
    action: "Roll Quantity Reset",
  });
}

// Stack Quantity Selected
export function trackStackQuantitySelected(stackOption: StackSelection, pageType: "pdp" | "cart" = "pdp") {
  if (stackOption.rollCount === undefined || stackOption.rollQty === undefined) return;
  analytics.track("Item Clicked", {
    numStacks: stackOption.rollCount,
    itemsPerStack: stackOption.rollQty,
    pageType,
    action: "Bundle Quantity Selected",
  });
}

// Upload Artwork Clicked — 1:1 parity with sg-store NoArtUploadDisplay.tsx
export function trackUploadArtworkClicked() {
  analytics.track("Item Clicked", {
    pageType: "pdp",
    action: "Upload Artwork Clicked",
  });
}

// Track Advanced Options on PDP expanded or collapsed
export function trackAdvancedOptionsToggled() {
  analytics.track("Item Clicked", {
    pageType: "pdp",
    action: "Advanced Options Toggled",
  });
}

// Artwork Uploaded — 1:1 parity with sg-store NoArtUploadDisplay.tsx

interface ArtworkUploadedInput {
  filename: string;
  filetype: string;
  filesize: number;
  url: string;
}

export function trackArtworkUploaded(input: ArtworkUploadedInput) {
  analytics.track("Item Clicked", {
    filename: input.filename,
    filetype: input.filetype,
    filesize: input.filesize,
    url: input.url,
    pageType: "pdp",
    action: "Artwork Uploaded",
  });
}

// Low DPI Warning Shown — 1:1 parity with sg-store LowDpi.tsx
export function trackLowDpiWarningShown(dpi: number, dpiText: string) {
  analytics.track("Item Clicked", {
    dpi,
    dpiText,
    pageType: "pdp",
    action: "Low DPI Warning Shown",
  });
}

// Notes Entered — 1:1 parity with sg-store SpecialInstructions.tsx
export function trackNotesEntered(notes: string) {
  analytics.track("Item Clicked", {
    notes,
    pageType: "pdp",
    action: "Notes Entered",
  });
}

// Save Item Clicked — 1:1 parity with sg-store SaveProgressButton.tsx
export function trackSaveItemClicked(isLoggedIn: boolean, request: SaveItemRequest) {
  analytics.track("Save Item Clicked", {
    isLoggedIn,
    request,
  });
}

// Item Saved for Later — 1:1 parity with sg-store SaveProgressButton.tsx

interface ItemSavedInput {
  request: SaveItemRequest;
  response: SaveItemResponse;
  isEditing: boolean;
}

export function trackItemSavedForLater({ request, response, isEditing }: ItemSavedInput) {
  const eventName = isEditing ? "Item Saved for Later Edited" : "Item Saved for Later";
  analytics.track(eventName, {
    ...request,
    customer: {
      customerId: response.accountsCustomer.id,
      email: response.accountsCustomer.email,
    },
  });
}

// EmailPromoModal events

export type PopupComponentType = "emailPromoModal";

/** Radix collapses all three into one close, so the modal tracks which one fired. */
export type DismissMethod = "button" | "scrim" | "escape";

interface PopupDisplayedInput {
  popupId: string;
  componentType: PopupComponentType;
  triggerType: TriggerLabel;
}

export function trackPopupDisplayed({ popupId, componentType, triggerType }: PopupDisplayedInput) {
  analytics.track("Popup Displayed", {
    popupId,
    componentType,
    triggerType,
    pagePath: window.location.pathname,
  });
}

interface PopupDismissedInput {
  popupId: string;
  componentType: PopupComponentType;
  timeVisibleMs: number;
  dismissMethod: DismissMethod;
}

export function trackPopupDismissed({ popupId, componentType, timeVisibleMs, dismissMethod }: PopupDismissedInput) {
  analytics.track("Popup Dismissed", { popupId, componentType, timeVisibleMs, dismissMethod });
}

interface PopupSuppressedInput {
  popupId: string;
  componentType: PopupComponentType;
  suppressionReason: PromoSuppressionReason;
}

export function trackPopupSuppressed({ popupId, componentType, suppressionReason }: PopupSuppressedInput) {
  analytics.track("Popup Suppressed", { popupId, componentType, suppressionReason });
}

// CTA Button Clicked

interface CTAButtonClickedInput {
  name: string;
  destinationUrl: string;
  variant: string;
  internalId?: string;
  popupId?: string; // Only used in promo modal
}

export function trackCTAButtonClicked({ name, destinationUrl, variant, internalId, popupId }: CTAButtonClickedInput) {
  analytics.track("Item Clicked", {
    action: "CTA Button Clicked",
    name,
    destinationUrl,
    variant,
    url: window.location.href,
    ...(internalId !== undefined && { internalId }),
    ...(popupId !== undefined && { popupId }),
  });
}

// Related Product Clicked

interface RelatedProductClickedInput {
  title: string;
  slug: string;
  sku?: string;
}

export function trackRelatedProductClicked({ title, slug, sku }: RelatedProductClickedInput) {
  analytics.track("Item Clicked", {
    action: "Related Product Clicked",
    pageType: "pdp",
    name: title,
    sku,
    destinationUrl: `/${slug}`,
    url: window.location.href,
  });
}

// Item Removed from Cart — 1:1 parity with sg-store cart removal tracking
export function trackItemRemovedFromCart(item: CartItemOutputDto) {
  analytics.track("Item Removed from Cart", {
    itemId: item.id,
    skus: item.sku,
    pageType: "cart",
  });
}

// Cart Item Edit Clicked — track when user clicks edit on a cart item
export function trackCartItemEditClicked(item: CartItemOutputDto, editUrl: string) {
  analytics.track("Cart Item Edit Clicked", {
    itemId: item.id,
    editUrl,
    marketingName: item.marketingName,
    pageType: "cart",
  });
}

// Cart Item Duplicated — track when user duplicates a cart item
export function trackItemDuplicated(item: CartItemOutputDto) {
  analytics.track("Cart Item Duplicated", {
    itemId: item.id,
    marketingName: item.marketingName,
    quantity: item.quantity,
    pageType: "cart",
  });
}

// Identify — user traits sent to Segment
export function identifyReturningCustomer(isReturningCustomer: boolean) {
  analytics.identify({ isReturningCustomer });
}

// The PII Meta's advanced matching can hash (em/fn/ln/ph). Sent as discrete traits
// because the Facebook Pixel destination maps each one individually — a combined
// `name` trait populates neither fn nor ln. Absent values are omitted rather than
// sent as undefined so a partially-known user still matches on what we do have.
interface PiiTraitsInput {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

function buildPiiTraits({ email, firstName, lastName, phone }: PiiTraitsInput) {
  return {
    ...(email ? { email: email.toLowerCase() } : {}),
    ...(firstName ? { firstName } : {}),
    ...(lastName ? { lastName } : {}),
    ...(phone ? { phone } : {}),
  };
}

// A single login is observable from several places (the identity sync, the guest-cart
// merge, the checkout email step), and each identify re-sends hashed PII. Collapsing on
// the payload keeps one login/signup to one identify while still allowing a genuine
// change — new traits, or a different user — through.
let lastIdentifySignature: string | undefined;

function identifyOnce(customerId: string | undefined, traits?: Record<string, unknown>) {
  const signature = JSON.stringify({ customerId, traits });
  if (signature === lastIdentifySignature) return;
  lastIdentifySignature = signature;

  if (!customerId) {
    analytics.identify(traits);
    return;
  }
  if (!traits) {
    analytics.identify(customerId);
    return;
  }
  analytics.identify(customerId, traits);
}

// Sets Segment's user identity to the authenticated customer. Driven by the auth
// claims store so every event after login attributes to the correct user. Used as the
// fallback when the account service can't supply traits.
export function identifyUser(customerId: string) {
  identifyOnce(customerId);
}

// The traits that describe an authenticated user. Shared with the Ninetailed identity
// sync so both tools receive the same payload for the same customer id — that overlap
// is what lets the Segment data connector resolve the two profiles to one person.
export function buildAuthenticatedCustomerTraits(customer: AccountCustomerOutputDto) {
  const name = customer.fullName || [customer.firstName, customer.lastName].filter(Boolean).join(" ");
  return {
    ...buildPiiTraits(customer),
    ...(name ? { name } : {}),
    gaClientId: CookieHelper.getCookie("_ga"),
  };
}

// Attaches the authenticated user's PII to the pixel session at login/signup, so every
// event that follows carries hashed identifiers Meta can match on.
export function identifyAuthenticatedCustomer(customerId: string, customer: AccountCustomerOutputDto) {
  identifyOnce(customerId, buildAuthenticatedCustomerTraits(customer));
}

// Clears Segment's persisted identity on logout, but only when one is actually
// set — so genuine guest browsing doesn't churn the anonymousId on every load. Concurrent
// callers share one check: two resets would mint two anonymousIds.
let pendingReset: Promise<void> | undefined;

export function resetIdentityIfStale() {
  pendingReset ??= resetStaleIdentity().finally(() => {
    pendingReset = undefined;
  });
  return pendingReset;
}

async function resetStaleIdentity() {
  const user = await analytics.user();
  if (user.id()) {
    analytics.reset();
    // Otherwise signing back into the same account would be swallowed as a duplicate.
    lastIdentifySignature = undefined;
  }
}

// Anonymous ID used to correlate this visitor's identity across Segment and
// downstream tools (e.g. as the Amplitude device id).
export async function getAnonymousId(): Promise<string | undefined> {
  const user = await analytics.user();
  return user.anonymousId() ?? undefined;
}

// Enriches the user with the PII traits available on the cart customer — the earliest
// point a guest becomes matchable, since they type their email at the checkout email
// step. A real customer id is only sent once it differs from the guest id (i.e. after
// the first guest checkout); before that the traits attach to the anonymous id alone.
export function identifyCustomer(customer: CustomerOutputDto) {
  const traits = {
    ...buildPiiTraits(customer),
    gaClientId: CookieHelper.getCookie("_ga"),
    isReturningCustomer: customer.isReturningCustomer,
    ...(customer.guest || !customer.firstName || !customer.lastName
      ? {}
      : { name: `${customer.firstName} ${customer.lastName}` }),
  };
  if (customer.customerId && customer.customerId !== customer.guestId) {
    identifyOnce(customer.customerId, traits);
    return;
  }
  identifyOnce(undefined, traits);
}

// Lead Form Completed — identify + track for the website email subscribe flow

/**
 * The popup's email submissions stay on these shared events and carry the popup as
 * properties, rather than forking into popup-only event names. `popupId` present means the
 * submission came from the promo modal — the PRD's popup_email_submitted / popup_email_error.
 */
interface LeadFormContext {
  source?: string;
  popupId?: string;
  timeVisibleMs?: number;
}

interface LeadFormCompletedInput extends LeadFormContext {
  customerId: string;
  email: string;
  firstName: string;
  lastName: string;
}

/** The optional half of both lead form events, compacted once (the `buildPiiTraits` pattern). */
function buildLeadFormContext({ source, popupId, timeVisibleMs }: LeadFormContext) {
  return {
    ...(source && { source }),
    ...(popupId !== undefined && { popupId }),
    ...(timeVisibleMs !== undefined && { timeVisibleMs }),
  };
}

export function identifyLeadFormCompleted({ customerId, email, firstName, lastName }: LeadFormCompletedInput) {
  analytics.identify(customerId, { email, firstName, lastName });
}

export function trackLeadFormCompleted({ customerId, email, firstName, lastName, ...context }: LeadFormCompletedInput) {
  analytics.track("Lead Form Completed", {
    customerId,
    email,
    firstName,
    lastName,
    ...buildLeadFormContext(context),
  });
}

interface LeadFormErrorInput extends LeadFormContext {
  errorType: "validation" | "network";
}

export function trackLeadFormError({ errorType, ...context }: LeadFormErrorInput) {
  analytics.track("Lead Form Error", { errorType, ...buildLeadFormContext(context) });
}

// Viewport Impression — 1:1 parity with sg-store useTrackSegmentInViewport
export function trackEvent(eventName: string, properties?: Record<string, unknown>) {
  analytics.track(eventName, properties);
}

// Variant Viewed — fires when a visitor sees a non-baseline Ninetailed
// personalization/experiment variant. Used to measure exposure for lift analysis.

export interface VariantViewedInput {
  experienceId: string;
  experienceName?: string;
  variantId: string;
  variantName: string;
  variantIndex: number;
}

export function trackVariantViewed(input: VariantViewedInput) {
  analytics.track("Variant Viewed", { ...input });
}

// Navigation Clicked

interface NavItemClickedInput {
  label: string;
  destinationUrl: string;
  level: 1 | 2 | 3;
}

export function trackNavItemClicked({ label, destinationUrl, level }: NavItemClickedInput) {
  analytics.track("Navigation Clicked", {
    navItem: label,
    navType: level === 1 ? "topNav" : "subNav",
    navDestination: destinationUrl,
  });
}

interface FooterNavItemClickedInput {
  label: string;
  destinationUrl: string;
}

export function trackFooterNavItemClicked({ label, destinationUrl }: FooterNavItemClickedInput) {
  analytics.track("Navigation Clicked", {
    navItem: label,
    navType: "footer",
    navDestination: destinationUrl,
  });
}

// Promo Applied — 1:1 parity with sg-store useAddCouponCode

interface CouponAppliedInput {
  cartId: string;
  couponCode: string;
  couponStatus: CouponStatus;
  pricing: PricingOutputDto;
}

export function trackCouponApplied({ cartId, couponCode, couponStatus, pricing }: CouponAppliedInput) {
  analytics.track("Promo Applied", {
    cartId,
    couponCode,
    couponStatus,
    pricing,
  });
}

// Checkout Started — 1:1 parity with sg-store CheckoutHelper.trackCheckoutStarted
export function trackCheckoutStarted(cart: CartOutputDto) {
  analytics.track("Checkout Started", {
    coupon: cart.couponCode,
    currency: "USD",
    discount: cart.pricing.discountTotal,
    order_id: cart.id,
    products: cart.items,
    value: cart.pricing.grandTotal,
  });
}

// Checkout Step Completed — 1:1 parity with sg-store CheckoutHelper.trackCheckoutStepCompleted

interface CheckoutStepCompletedInput {
  cart: CartOutputDto;
  stepName: string;
  stepNumber: number;
}

export function trackCheckoutStepCompleted({ cart, stepName, stepNumber }: CheckoutStepCompletedInput) {
  analytics.track("Checkout Step Completed", {
    checkoutId: cart.id,
    step: stepName,
    stepNumber,
    products: cart.items,
  });

  const stepIndividualEventName = getStepIndividualEventName(stepNumber);
  if (stepIndividualEventName) {
    trackIndividualCheckoutStepCompleted({ cart, eventName: stepIndividualEventName });
  }
}

// Record checkout steps as individual events for some downstream sources

interface IndividualCheckoutStepCompletedInput {
  cart: CartOutputDto;
  eventName: string;
}

export function trackIndividualCheckoutStepCompleted({ cart, eventName }: IndividualCheckoutStepCompletedInput) {
  analytics.track(eventName, {
    checkoutId: cart.id,
    products: cart.items,
  });
}

// Optional Add-On Clicked

interface OptionalAddOnClickedInput {
  sku: string;
  position: number;
}

export function trackOptionalAddOnClicked({ sku, position }: OptionalAddOnClickedInput) {
  analytics.track("Optional Add-On Clicked", {
    sku,
    position,
    pageType: "cart",
  });
}

// Order Completed — 1:1 parity with sg-frontend-common SegmentHelper.trackOrderCompleted
// Field names (CustomerStatus, GAclientId, fb_login_id, revenue_client, etc.) are part
// of the contract with Impact.com / Segment / GTM — do not rename.

const orderCompletedStorageKey = (cartId: string) => `order-completed-${cartId}`;

function hasOrderCompletedFired(cartId: string): boolean {
  try {
    return localStorage.getItem(orderCompletedStorageKey(cartId)) === "true";
  } catch {
    return false;
  }
}

function markOrderCompletedFired(cartId: string): void {
  try {
    localStorage.setItem(orderCompletedStorageKey(cartId), "true");
  } catch {
    // No-op
    // Storage write blocked — worst case we track again on a future retry for this cart.
  }
}

function translateOrderCompletedItem(item: CartItemOutputDto) {
  return {
    adhesive: item.adhesive,
    amount:
      item.itemPrice - item.discountAmount + item.taxAmount + (item.expeditedFee ?? 0) + (item.expeditedFeeTax ?? 0),
    amountQuantity: 1,
    artworkPage: item.artworkPage,
    category: translateItemCategory(item.sku.product),
    couponDiscountAmount: item.couponDiscountAmount,
    couponThresholdMessage: item.couponThresholdMessage,
    created: item.created,
    description: item.description,
    discountAmount: item.discountAmount,
    discountDescription: item.discountDescription,
    discountName: item.discountName,
    discountedItemPrice: item.itemPrice - item.discountAmount,
    fileBucket: item.fileBucket,
    fileUrl: item.fileUrl,
    finish: item.finish,
    height: item.height,
    id: item.id,
    image_url: ThumbnailGenerator.getUrl(item.fileBucket, item.fileUrl, item.artworkPage),
    isApplicableToShowcaseDiscount: item.isApplicableToShowcaseDiscount,
    isReorder: item.isReorder,
    itemId: item.id,
    itemNotes: item.itemNotes,
    itemPrice: item.itemPrice,
    laminate: item.laminate,
    marketingName: item.marketingName,
    material: item.material,
    noArt: item.noArt,
    orientation: item.orientation,
    position: item.position,
    product: item.product,
    quantity: item.quantity,
    shape: item.shape,
    shortItemId: ItemFormatter.itemUUIDFormatter(item.id),
    siteWideDiscountAmount: item.siteWideDiscountAmount,
    siteWideDiscountDescription: item.siteWideDiscountDescription,
    siteWideDiscountName: item.siteWideDiscountName,
    sku: {
      adhesive: item.sku.adhesive,
      finish: item.sku.finish,
      inferredMarketingName: item.sku.inferredMarketingName,
      laminate: item.sku.laminate,
      marketingName: item.sku.marketingName,
      material: item.sku.material,
      orientation: item.sku.orientation,
      product: item.sku.product,
      shape: item.sku.shape,
    },
    specId: item.specId,
    taxAmount: item.taxAmount,
    updated: item.updated,
    websiteCartItemId: item.id,
    websiteProductId: item.sku.marketingName,
    width: item.width,
  };
}

export function trackOrderCompleted(cart: CartOutputDto, paymentMethod: PaymentMethodType, usedSavedCard: boolean) {
  if (hasOrderCompletedFired(cart.id)) return;
  markOrderCompletedFired(cart.id);

  const adjustmentTimestamp = new Date();
  adjustmentTimestamp.setHours(adjustmentTimestamp.getHours() + 1);

  analytics.track("Order Completed", {
    adjustmentTimestamp,
    amount: cart.pricing.grandTotal,
    checkoutId: cart.id,
    city: cart.billingInfo.billingAddress?.city,
    country: cart.billingInfo.billingAddress?.country,
    coupon: cart.couponCode,
    couponCode: cart.couponCode,
    currency: "USD",
    CustomerStatus: cart.customer.isReturningCustomer ? "anExistingCustomer" : "aNewCustomer",
    discount: cart.pricing.discountTotal,
    discountTotal: cart.pricing.discountTotal,
    email: cart.customer.email?.toLowerCase() ?? null,
    experiments: cart.experiments,
    fb_login_id: CookieHelper.getCookie("fblo_"),
    fbc: CookieHelper.getCookie("_fbc"),
    fbp: CookieHelper.getCookie("_fbp"),
    firstName: cart.customer.firstName,
    GAclientId: CookieHelper.getCookie("_ga"),
    gclid: CookieHelper.getCookie("gclid"),
    isReturningCustomer: cart.customer.isReturningCustomer,
    lastName: cart.customer.lastName,
    new_customer: !cart.customer.isReturningCustomer,
    orderId: cart.id,
    orderCouponDiscountAmount: cart.pricing.orderCouponDiscountAmount,
    orderReason: cart.cartReason,
    paymentMethod,
    phone: cart.customer.phone,
    postalCode: cart.billingInfo.billingAddress?.postalCode,
    products: cart.items.map(item => translateOrderCompletedItem(item)),
    revenue_client: cart.pricing.subTotal - cart.pricing.discountTotal,
    samplePackOnly: cart.samplePackOnly,
    shipping: cart.pricing.shippingTotal,
    shippingMethod: cart.shippingInfo.shippingMethod,
    shortOrderId: cart.shortId,
    state: cart.billingInfo.billingAddress?.state,
    subTotal: cart.pricing.subTotal,
    tax: (cart.pricing.salesTax ?? 0) + (cart.pricing.shippingTax ?? 0),
    total: cart.pricing.grandTotal,
    value: cart.pricing.grandTotal,
    isExpedited: cart.isExpedited,
    usedSavedCard,
  });
}

// Saved addresses & payment methods (account + checkout)

export function trackAddressSaved(source: SavedInfoSource) {
  analytics.track("Address Saved", { source });
}

export function trackAddressEdited(source: SavedInfoSource) {
  analytics.track("Saved Address Edited", { source });
}

export function trackAddressDeleted(source: SavedInfoSource) {
  analytics.track("Saved Address Deleted", { source });
}

export function trackPaymentMethodSaved(source: SavedInfoSource) {
  analytics.track("Payment Method Saved", { source });
}

export function trackPaymentMethodDeleted(source: SavedInfoSource) {
  analytics.track("Saved Payment Method Deleted", { source });
}

// Fires when the user submits a query from the search panel.
export interface SearchSubmittedPayload {
  query: string;
  resultCount: number;
  source: "autocomplete" | "submit";
}

export function buildSearchSubmittedPayload({
  query,
  resultCount,
  source,
}: SearchSubmittedPayload): SearchSubmittedPayload {
  return { query, resultCount, source };
}

export function trackSearchSubmitted(input: SearchSubmittedPayload) {
  analytics.track("Search Submitted", buildSearchSubmittedPayload(input));
}

// Fires when a query returns zero suggestions and zero products.
export function trackSearchNoResults({ query }: { query: string }) {
  analytics.track("Search No Results", { query });
}

// Products Searched — Segment V2 ecommerce spec. Fires once per executed search.
export function trackProductsSearched({ query }: { query: string }) {
  analytics.track("Products Searched", { query });
}

// Fires when a link in the no-results panel is clicked.
export function trackSearchNoResultsLinkClicked({
  target,
  query,
}: {
  target: "product-request" | "contact-support";
  query: string;
}) {
  analytics.track("Item Clicked", { action: "Search No Results Link Clicked", target, query });
}

// Fires when an empty-state popular search chip is clicked.
interface PopularSearchClickedInput {
  term: string;
  position: number;
}

export function trackPopularSearchClicked({ term, position }: PopularSearchClickedInput) {
  analytics.track("Item Clicked", {
    action: "Popular Search Clicked",
    term,
    position,
  });
}

// ===========================================================================
// V2 Ecommerce events — Product List Viewed / Filtered / Product Clicked.
// https://segment.com/docs/connections/spec/ecommerce/v2/
// `position` is 1-based per the spec examples.
// ===========================================================================

export interface EcommerceProduct {
  product_id: string;
  sku: string;
  category?: string;
  name: string;
  price?: number;
  position: number;
  url: string;
  image_url?: string;
}

export function toEcommerceProduct(hit: ProductHit, position: number): EcommerceProduct {
  const product: EcommerceProduct = {
    product_id: hit.sku,
    sku: hit.sku,
    name: hit.displayName || hit.name,
    position,
    url: `/${hit.slug}`,
  };
  if (hit.category) product.category = hit.category;
  if (typeof hit.startingPrice === "number") product.price = hit.startingPrice;
  if (hit.imageUrl) product.image_url = hit.imageUrl;
  return product;
}

export interface ProductListFilterEntry {
  type: string;
  value: string;
}

export interface ProductListViewedInput {
  listId: string;
  category?: string;
  products: EcommerceProduct[];
}

export function trackProductListViewed({ listId, category, products }: ProductListViewedInput) {
  // category omitted from the wire when undefined (JSON drops undefined keys).
  analytics.track("Product List Viewed", { list_id: listId, category, products });
}

export interface ProductListFilteredInput {
  listId: string;
  category?: string;
  filters: ProductListFilterEntry[];
  products: EcommerceProduct[];
}

export function trackProductListFiltered({ listId, category, filters, products }: ProductListFilteredInput) {
  analytics.track("Product List Filtered", { list_id: listId, category, filters, products });
}

export function trackProductClicked(product: EcommerceProduct) {
  analytics.track("Product Clicked", product);
}

// Order Completed — GTM dataLayer push (separate channel from Segment)
export function pushOrderCompletedDataLayer(cart: CartOutputDto, onComplete: () => void) {
  pushDataLayer({
    event: "sg-order-completed",
    value: cart.pricing.grandTotal,
    orderId: cart.id,
    eventCallback: onComplete,
  });
}
